import React, { useState, useEffect } from 'react';
import { useToast, Flex, Text, Spinner, Box, IconButton } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useMutation } from '@apollo/client';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { GroupChannel } from '@sendbird/uikit-react/GroupChannel';
import { SETUP_SENDBIRD_CHAT } from '../../graphql/sendbird';
import colors from '../../helpers/colors';
import theme from '../../theme';
import '@sendbird/uikit-react/dist/index.css';
import { avatarUrl } from '../../helpers/utils';
import ProfileImage from '../common/ProfileImage';

const ChatDrawer = ({ isOpen, onClose, currentUser, candidate, existingChannelUrl }) => {
  const [showChat, setShowChat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [channelUrl, setChannelUrl] = useState(existingChannelUrl || '');
  const [accessToken, setAccessToken] = useState('');
  const [appId, setAppId] = useState('');
  const toast = useToast();
  const [setupSendbirdChat] = useMutation(SETUP_SENDBIRD_CHAT);
  const [webhookEvents] = useState([]);

  const setupConnection = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (!currentUser?.id || !candidate?.userId) {
        throw new Error('User and candidate are required');
      }

      const response = await setupSendbirdChat({
        variables: {
          currentUserId: currentUser.id,
          currentNickname: `${currentUser.firstName} ${currentUser.lastName}`,
          currentProfileUrl: currentUser.profileImageUrl || avatarUrl(currentUser.fullName),
          candidateUserId: candidate.userId,
          candidateNickname: candidate.fullName || '',
          candidateProfileUrl: candidate.profileImageUrl || avatarUrl(candidate.fullName),
        }
      });

      const result = response.data?.setupSendbirdChat;
      if (!result?.user || !result?.channel) {
        throw new Error('Invalid response from setupSendbirdChat');
      }

      setAccessToken(result.user.accessToken);
      setAppId(result.user.appId);
      setChannelUrl(existingChannelUrl || result.channel.channelUrl);
      setShowChat(true);
    } catch (error) {
      console.error('Error setting up chat:', error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [setupSendbirdChat, currentUser, candidate, existingChannelUrl, toast, onClose]);

  useEffect(() => {
    if (isOpen && candidate?.userId) {
      setupConnection();
    }
  }, [isOpen, candidate?.userId, setupConnection]);

  const colorSet = {
    '--sendbird-dark-primary-500': theme.colors.brand[100],
    '--sendbird-dark-primary-400': theme.colors.brand[200],
    '--sendbird-dark-primary-300': theme.colors.brand[500],
    '--sendbird-dark-primary-200': theme.colors.brand[500],
    '--sendbird-dark-primary-100': theme.colors.brand[300],
  };

  const renderWebhookEvents = () => {
    if (!showChat) return null;
    return (
      <Box position="absolute" bottom="20px" right="20px" zIndex={1000}>
        {webhookEvents.slice(-5).map((event, index) => (
          <Box key={index} bg={colors.greyMedium} p={2} mb={2} borderRadius="md" fontSize="sm">
            <Text>{event.type}</Text>
            {event.message && <Text>{event.message}</Text>}
          </Box>
        ))}
      </Box>
    );
  };

  const getChatHeader = () => (
    <Flex align="center" gap={3} p={4} borderBottom={`1px solid ${colors.greyMedium}`}>
      <ProfileImage name={candidate?.fullName} imageUrl={candidate?.profileImageUrl} size="36px" />
      <Text fontSize="16px" fontWeight="bold">{candidate?.fullName}</Text>
    </Flex>
  );

  return (
    <Box
      position="fixed"
      top="0"
      right={isOpen ? '0' : '-520px'}
      height="100vh"
      width="500px"
      backgroundColor={colors.greyDark}
      boxShadow="lg"
      zIndex="1500"
      transition="right 0.3s ease"
      overflow="hidden"
    >
      <IconButton
        icon={<CloseIcon boxSize={4} />}
        onClick={onClose}
        position="absolute"
        top="14px"
        right="10px"
        size="sm"
        zIndex="10"
        bg="transparent"
        _hover={{ bg: "gray.600" }}
        aria-label="Close chat drawer"
      />
      {isLoading ? (
        <Box h="100%" display="flex" alignItems="center" justifyContent="center">
          <Spinner size="xl" />
        </Box>
      ) : showChat ? (
        <>
          <SendbirdProvider
            theme="dark"
            appId={appId}
            userId={currentUser?.id}
            profileUrl={currentUser?.profileImageUrl}
            accessToken={accessToken}
            colorSet={colorSet}
          >
            <GroupChannel
              channelUrl={channelUrl}
              renderChannelHeader={getChatHeader}
              scrollBehavior='smooth'
            />
          </SendbirdProvider>
          {renderWebhookEvents()}
        </>
      ) : null}
    </Box>
  );
};

export default ChatDrawer;
